/**
 * Synthetic multi-project "digital twin" dataset (financial, schedule, ops, safety).
 * Deterministic per (projectId, as-of date): same day → same numbers; new day → evolved snapshot.
 */

function hashSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

function monthKeys(fromYm, toYm) {
  const out = [];
  const [fy, fm] = fromYm.split('-').map(Number);
  const [ty, tm] = toYm.split('-').map(Number);
  let y = fy;
  let m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** Last month included in twin-forward projection roll-ups (showcases 2026 Q3–Q4 + full-year 2027). */
const TWIN_FORECAST_END_YM = '2027-12';

function bumpYm(ym) {
  const [y, m] = ym.split('-').map(Number);
  let mm = m + 1;
  let yy = y;
  if (mm > 12) {
    mm = 1;
    yy += 1;
  }
  return `${yy}-${String(mm).padStart(2, '0')}`;
}

/**
 * Forward months after as-of through `TWIN_FORECAST_END_YM` for QoQ / prediction demos (deterministic).
 */
function appendForecastTwinMonths(projectId, asOfYm, meta, monthlyActual, revisedContract) {
  const startYm = bumpYm(asOfYm);
  if (!monthlyActual.length || startYm > TWIN_FORECAST_END_YM) return [];

  const fcMonths = monthKeys(startYm, TWIN_FORECAST_END_YM);
  const rng = mulberry32(hashSeed(`${projectId}|forecast|${asOfYm}`));
  const lastA = monthlyActual[monthlyActual.length - 1];

  let prevCost = lastA.costToDateM;
  let prevPctMonotone = lastA.pctComplete / 100;
  let cumulativeObs = lastA.cumulativeSafetyObs;
  let cumulativeIncidents = lastA.cumulativeIncidents;

  const totalMonths = meta.durationMonths;
  const startIdxGlobal =
    Number(meta.startYm.slice(0, 4)) * 12 + Number(meta.startYm.slice(5, 7));

  const out = [];
  for (let mi = 0; mi < fcMonths.length; mi += 1) {
    const ym = fcMonths[mi];
    const idxGlobal = Number(ym.slice(0, 4)) * 12 + Number(ym.slice(5, 7));
    const elapsed = idxGlobal - startIdxGlobal + 1;
    const t = clamp(elapsed / totalMonths, 0, 1.15);

    const sCurve = 1 / (1 + Math.exp(-8 * (t - 0.45)));
    let pctComplete = clamp(sCurve * (0.92 + rng() * 0.06), 0, 0.985);
    pctComplete = Math.max(prevPctMonotone, pctComplete);
    prevPctMonotone = pctComplete;

    const cpiBase = 0.98 + t * 0.12 + (rng() - 0.5) * 0.07;
    const spiBase = 0.62 + t * 0.35 + (rng() - 0.5) * 0.07;
    const cpi = clamp(cpiBase, 0.92, 1.28);
    const spi = clamp(spiBase, 0.45, 1.12);

    const earnedValueM = revisedContract * pctComplete * (0.97 + rng() * 0.04);
    let costToDateM = clamp(earnedValueM / Math.max(0.65, cpi), 0, revisedContract * 1.06);
    costToDateM = Math.max(prevCost, costToDateM);
    const monthCostM = Math.max(0.05, costToDateM - prevCost);
    prevCost = costToDateM;

    const prevRow = out.length ? out[out.length - 1] : lastA;
    const prevBilled = prevRow.billedCumulativeM;
    const billPct = clamp(pctComplete * (0.88 + rng() * 0.12), 0, 1);
    let billedCumulativeM = Math.round(revisedContract * billPct * 10) / 10;
    billedCumulativeM = Math.max(prevBilled, billedCumulativeM);
    const billedM = Math.round((billedCumulativeM - prevBilled) * 10) / 10;

    cumulativeObs += Math.round(140 + rng() * 180 + monthCostM * 6);
    if (rng() < 0.14) cumulativeIncidents += rng() < 0.35 ? 2 : 1;

    const docActivityIndex = Math.round(
      clamp(38 + rng() * 70 + monthCostM * 2 + elapsed * 1.2, 12, 220),
    );

    out.push({
      month: ym,
      quarterKey: ymToQuarterKey(ym),
      costMonthM: Math.round(monthCostM * 10) / 10,
      costToDateM: Math.round(costToDateM * 10) / 10,
      billedM: Math.round(billedM * 10) / 10,
      billedCumulativeM: Math.round(billedCumulativeM * 10) / 10,
      pctComplete: Math.round(pctComplete * 1000) / 10,
      cpi: Math.round(cpi * 1000) / 1000,
      spi: Math.round(spi * 1000) / 1000,
      cumulativeSafetyObs: cumulativeObs,
      cumulativeIncidents,
      docActivityIndex,
      isProjected: true,
    });
  }
  return out;
}

function ymToQuarterKey(ym) {
  const [y, mo] = ym.split('-').map(Number);
  const q = Math.ceil(mo / 3);
  return `${y}-Q${q}`;
}

function daysInCalendarMonthYm(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

/** Whole calendar days since the first of startYm through as-of (local civil dates from ISO strings). */
function calendarDaysSinceStartYm(startYm, asOfISO) {
  const [sy, sm] = startYm.split('-').map(Number);
  const [y, m, d] = asOfISO.split('-').map(Number);
  const t0 = new Date(sy, sm - 1, 1).getTime();
  const t1 = new Date(y, m - 1, d).getTime();
  return Math.max(0, Math.round((t1 - t0) / 86400000));
}

/**
 * Progress the final monthly row by day-of-month so "next day" monotonically increases
 * completion, cost, billings, safety obs, etc. within the same calendar month.
 */
function applyIntraMonthTwinDay(monthly, asOfISO, revisedContractM) {
  if (!monthly.length) return;
  const asOfYm = asOfISO.slice(0, 7);
  const lastIdx = monthly.length - 1;
  const L = monthly[lastIdx];
  if (L.month !== asOfYm) return;

  const dom = Number(asOfISO.slice(8, 10));
  const dim = daysInCalendarMonthYm(asOfYm);
  const frac = clamp(dom / dim, 1 / dim, 1);

  const P = lastIdx > 0 ? monthly[lastIdx - 1] : null;
  const pPct = P?.pctComplete ?? 0;
  const pCost = P?.costToDateM ?? 0;
  const pBill = P?.billedCumulativeM ?? 0;
  const pObs = P?.cumulativeSafetyObs ?? 0;
  const pInc = P?.cumulativeIncidents ?? 0;
  const pCpi = P?.cpi ?? L.cpi;
  const pSpi = P?.spi ?? L.spi;
  const pDoc = P?.docActivityIndex ?? L.docActivityIndex;

  const tPct = L.pctComplete;
  const tCost = L.costToDateM;
  const tBill = L.billedCumulativeM;
  const tObs = L.cumulativeSafetyObs;
  const tInc = L.cumulativeIncidents;
  const tCpi = L.cpi;
  const tSpi = L.spi;
  const tDoc = L.docActivityIndex;

  // kpis store pct as 0–98.5 (percent points), not 0–0.985 fractions — use /10 rounding here only.
  L.pctComplete = Math.round((pPct + (tPct - pPct) * frac) * 10) / 10;
  L.pctComplete = Math.min(98.5, L.pctComplete);

  L.costToDateM = Math.round((pCost + (tCost - pCost) * frac) * 10) / 10;
  L.costToDateM = clamp(L.costToDateM, pCost, revisedContractM * 1.08);

  L.billedCumulativeM = Math.round((pBill + (tBill - pBill) * frac) * 10) / 10;
  L.billedCumulativeM = clamp(L.billedCumulativeM, pBill, revisedContractM);

  L.cumulativeSafetyObs = Math.round(pObs + (tObs - pObs) * frac);
  L.cumulativeSafetyObs = Math.max(pObs, L.cumulativeSafetyObs);

  L.cumulativeIncidents = Math.max(pInc, Math.floor(pInc + (tInc - pInc) * frac));

  L.cpi = Math.round((pCpi + (tCpi - pCpi) * frac) * 1000) / 1000;
  L.spi = Math.round((pSpi + (tSpi - pSpi) * frac) * 1000) / 1000;

  L.docActivityIndex = Math.round(pDoc + (tDoc - pDoc) * frac);
  L.docActivityIndex = Math.max(pDoc, L.docActivityIndex);

  L.costMonthM = Math.round(Math.max(0.1, L.costToDateM - pCost) * 10) / 10;
  L.billedM = Math.round(Math.max(0, L.billedCumulativeM - pBill) * 10) / 10;
}

function compareQuarterKeys(a, b) {
  const [ya, qa] = a.split('-Q').map(Number);
  const [yb, qb] = b.split('-Q').map(Number);
  return ya * 4 + qa - (yb * 4 + qb);
}

/** @typedef {{ id: string; code: string; name: string; subtitle: string; pm: string; division: string; company: string; status: string; startYm: string; targetCloseYm: string; contractOriginalM: number; approvedCOM: number; durationMonths: number; inProcore: boolean; }} ProjectMeta */

/** @type {ProjectMeta[]} */
export const DIGITAL_TWIN_PROJECTS = [
  {
    id: 'p26-fab',
    code: '26-FAB',
    name: 'Taylor Fab — MEP Package',
    subtitle: 'Hyperscale fab, Central Texas · twin calibrated to public-record milestones · $ figures illustrative',
    pm: 'Sam Rivera',
    division: 'Central Texas (Division 5)',
    company: 'SAMSUNG TAYLOR FAB (PUBLIC-RECORD DEMO)',
    status: 'Active / Open',
    startYm: '2024-09',
    targetCloseYm: '2027-06',
    contractOriginalM: 640,
    approvedCOM: 155,
    durationMonths: 34,
    inProcore: true,
  },
  {
    id: 'p25-111',
    code: '25-111',
    name: 'Acme Manufacturing P708',
    subtitle: 'Automotive Assembly Facility Expansion — Phase 2',
    pm: 'Alex Johnson',
    division: 'North Region (Division 1)',
    company: 'DEMO AUTOMOTIVE CORP',
    status: 'Active / Open',
    startYm: '2024-04',
    targetCloseYm: '2027-09',
    contractOriginalM: 135,
    approvedCOM: 30,
    durationMonths: 42,
    inProcore: false,
  },
  {
    id: 'p24-042',
    code: '24-042',
    name: 'Riverside Hyperscale Data Hall',
    subtitle: 'Tier III Colocation & Mechanical Yard',
    pm: 'Priya Nair',
    division: 'West Region (Division 3)',
    company: 'NEXUS CLOUD INFRA LLC',
    status: 'Active / Open',
    startYm: '2024-01',
    targetCloseYm: '2027-03',
    contractOriginalM: 210,
    approvedCOM: 45,
    durationMonths: 40,
    inProcore: true,
  },
  {
    id: 'p23-088',
    code: '23-088',
    name: 'Harbor Logistics & Cold Storage',
    subtitle: 'Port-adjacent warehouse + refrigerated docks',
    pm: 'Marcus Chen',
    division: 'East Region (Division 2)',
    company: 'ATLANTIC SUPPLY CHAIN PARTNERS',
    status: 'Active / Open',
    startYm: '2024-06',
    targetCloseYm: '2027-01',
    contractOriginalM: 88,
    approvedCOM: 12,
    durationMonths: 31,
    inProcore: false,
  },
  {
    id: 'p25-203',
    code: '25-203',
    name: 'Metro General Hospital Wing',
    subtitle: 'ICU expansion + central utility plant tie-in',
    pm: 'Jordan Ellis',
    division: 'Central Region (Division 4)',
    company: 'METRO HEALTH SYSTEMS',
    status: 'Active / Open',
    startYm: '2024-08',
    targetCloseYm: '2027-07',
    contractOriginalM: 162,
    approvedCOM: 18,
    durationMonths: 36,
    inProcore: true,
  },
  {
    id: 'p24-501',
    code: '24-501',
    name: 'Desert Sun Solar Field Phase 3',
    subtitle: 'PV arrays, substation, and gen-tie corridor',
    pm: 'Sam Okonkwo',
    division: 'South Region (Division 5)',
    company: 'HELIO FRONTIER ENERGY',
    status: 'Active / Open',
    startYm: '2024-09',
    targetCloseYm: '2027-06',
    contractOriginalM: 96,
    approvedCOM: 22,
    durationMonths: 34,
    inProcore: false,
  },
];

function pickMeta(projectId) {
  const m = DIGITAL_TWIN_PROJECTS.find((p) => p.id === projectId);
  if (!m) throw new Error(`Unknown digital twin project: ${projectId}`);
  return m;
}

/**
 * @param {string} projectId
 * @param {string} asOfISO date-only YYYY-MM-DD (local semantics via string compare on months)
 */
export function getProjectTwinData(projectId, asOfISO) {
  const meta = pickMeta(projectId);
  const asOfYm = asOfISO.slice(0, 7);

  const revisedContract = meta.contractOriginalM + meta.approvedCOM;
  const baseRng = mulberry32(hashSeed(`${projectId}|basecurve`));

  const months = monthKeys(meta.startYm, asOfYm);
  const monthly = [];
  let prevCost = 0;
  let prevPctMonotone = 0;
  let cumulativeObs = 0;
  let cumulativeIncidents = 0;

  const totalMonths = meta.durationMonths;
  const startIdxGlobal =
    Number(meta.startYm.slice(0, 4)) * 12 + Number(meta.startYm.slice(5, 7));

  for (let mi = 0; mi < months.length; mi += 1) {
    const ym = months[mi];
    const idxGlobal = Number(ym.slice(0, 4)) * 12 + Number(ym.slice(5, 7));
    const elapsed = idxGlobal - startIdxGlobal + 1;
    const t = clamp(elapsed / totalMonths, 0, 1.15);

    const sCurve = 1 / (1 + Math.exp(-8 * (t - 0.45)));
    let pctComplete = clamp(sCurve * (0.92 + baseRng() * 0.06), 0, 0.985);
    pctComplete = Math.max(prevPctMonotone, pctComplete);
    prevPctMonotone = pctComplete;

    // CPI = EV/AC (EVM). Keep cumulative CPI mostly ≥ 1 so job cost stays at or below earned value → plausible positive demo margins.
    const cpiBase = 0.98 + t * 0.12 + (baseRng() - 0.5) * 0.07;
    const spiBase = 0.62 + t * 0.35 + (baseRng() - 0.5) * 0.07;
    const cpi = clamp(cpiBase, 0.92, 1.28);
    const spi = clamp(spiBase, 0.45, 1.12);

    const earnedValueM = revisedContract * pctComplete * (0.97 + baseRng() * 0.04);
    let costToDateM = clamp(earnedValueM / Math.max(0.65, cpi), 0, revisedContract * 1.06);
    costToDateM = Math.max(prevCost, costToDateM);
    const monthCostM = Math.max(0.1, costToDateM - prevCost);
    prevCost = costToDateM;

    const prevBilled = monthly[monthly.length - 1]?.billedCumulativeM ?? 0;
    const billPct = clamp(pctComplete * (0.88 + baseRng() * 0.12), 0, 1);
    let billedCumulativeM = Math.round(revisedContract * billPct * 10) / 10;
    billedCumulativeM = Math.max(prevBilled, billedCumulativeM);
    const billedM = Math.round(
      (billedCumulativeM - (monthly[monthly.length - 1]?.billedCumulativeM ?? 0)) * 10,
    ) / 10;

    cumulativeObs += Math.round(180 + baseRng() * 260 + monthCostM * 8);
    if (baseRng() < 0.18) cumulativeIncidents += baseRng() < 0.35 ? 2 : 1;

    const docActivityIndex = Math.round(
      clamp(38 + baseRng() * 85 + monthCostM * 2.2 + elapsed * 1.4, 12, 220),
    );

    monthly.push({
      month: ym,
      quarterKey: ymToQuarterKey(ym),
      costMonthM: Math.round(monthCostM * 10) / 10,
      costToDateM: Math.round(costToDateM * 10) / 10,
      billedM: Math.round(billedM * 10) / 10,
      billedCumulativeM: Math.round(billedCumulativeM * 10) / 10,
      pctComplete: Math.round(pctComplete * 1000) / 10,
      cpi: Math.round(cpi * 1000) / 1000,
      spi: Math.round(spi * 1000) / 1000,
      cumulativeSafetyObs: cumulativeObs,
      cumulativeIncidents,
      docActivityIndex,
    });
  }

  applyIntraMonthTwinDay(monthly, asOfISO, revisedContract);

  const last = monthly[monthly.length - 1];
  if (!last) {
    return buildEmpty(meta, asOfISO, revisedContract);
  }

  const estTotalCostM = clamp(last.costToDateM / Math.max(0.04, last.pctComplete / 100), 0, revisedContract * 1.12);
  const estProfitM = revisedContract - estTotalCostM;
  const marginPct = revisedContract > 0 ? (estProfitM / revisedContract) * 100 : 0;
  const balanceToBillM = Math.max(0, revisedContract - last.billedCumulativeM);

  const vistaActualM = Math.round((last.costToDateM + 4 + baseRng() * 6) * 10) / 10;
  const socDeltaM = Math.round((vistaActualM - last.costToDateM) * 10) / 10;

  const financialHealth = clamp(Math.round(52 + last.cpi * 28 + (marginPct - 8) * 0.8), 0, 100);
  const scheduleHealth = clamp(Math.round(48 + last.spi * 32 + (last.pctComplete - 78) * 0.4), 0, 100);

  const daysSinceStart = calendarDaysSinceStartYm(meta.startYm, asOfISO);
  const chronOpsLift = daysSinceStart * 0.14;

  const moScale = 1 + baseRng() * 0.35;
  const rfi = Math.round((120 + elapsedMonths(meta, asOfYm) * 6.2) * moScale + chronOpsLift);
  const submittals = Math.round((70 + elapsedMonths(meta, asOfYm) * 3.5) * moScale + chronOpsLift * 0.62);
  const changeOrders = Math.round((40 + elapsedMonths(meta, asOfYm) * 2.1) * moScale + chronOpsLift * 0.38);
  const opsHealth = clamp(65 + Math.min(22, changeOrders / 5), 0, 100);

  const osha = Math.max(4, Math.round(6 + baseRng() * 6));
  const firstAid = Math.max(2, Math.round(3 + baseRng() * 5));
  const lostTimeDays = baseRng() > 0.82 ? Math.round(baseRng() * 3) : 0;
  const dom = Number(asOfISO.slice(8, 10));
  const soarTail = daysSinceStart * 0.22 + dom * 3.2;
  const soar = Math.round(last.cumulativeSafetyObs + soarTail);
  const manHours = Math.round(180000 + elapsedMonths(meta, asOfYm) * 14200 + daysSinceStart * 520);
  const trir = manHours > 0 ? Math.round((200000 * (osha + firstAid)) / manHours * 100) / 100 : 0;
  const daysSinceIncident =
    lostTimeDays > 0 ? 0 : Math.min(420, 26 + Math.floor(daysSinceStart * 0.55));
  const safetyHealth = clamp(92 - osha * 1.1 - firstAid * 0.5 + (lostTimeDays > 0 ? -15 : 8), 0, 100);

  const composite = Math.round((financialHealth + opsHealth + scheduleHealth + safetyHealth) / 4);

  const trades = [
    { name: 'Electrical', cpi: Math.round((last.cpi * (0.62 + baseRng() * 0.12)) * 1000) / 1000 },
    { name: 'M&R', cpi: Math.round((last.cpi * (1.05 + baseRng() * 0.18)) * 1000) / 1000 },
    { name: 'Piping', cpi: Math.round((last.cpi * (0.58 + baseRng() * 0.1)) * 1000) / 1000 },
  ];

  const earnedM = earnedShare(revisedContract, last.pctComplete);
  const wipM = Math.round((last.costToDateM - earnedM) * 10) / 10;

  const forecastRows = appendForecastTwinMonths(projectId, asOfYm, meta, monthly, revisedContract);
  monthly.push(...forecastRows);

  return {
    meta,
    dataAsOf: asOfISO,
    twinForecastThroughYm: TWIN_FORECAST_END_YM,
    forecastMonthsCount: forecastRows.length,
    sources: ['Vista ERP', 'SOC / Job Cost', 'Fulcrum Safety', meta.inProcore ? 'Procore' : 'SharePoint / FileBrain'],
    kpis: {
      revisedContractM: revisedContract,
      contractOriginalM: meta.contractOriginalM,
      approvedCOM: meta.approvedCOM,
      costToDateM: last.costToDateM,
      estRemainingM: Math.round(Math.max(0, estTotalCostM - last.costToDateM) * 10) / 10,
      pctComplete: last.pctComplete,
      estProfitM: Math.round(estProfitM * 10) / 10,
      estGrossMarginPct: Math.round(marginPct * 10) / 10,
      balanceToBillM: Math.round(balanceToBillM * 10) / 10,
      billedToDateM: last.billedCumulativeM,
      wipM,
    },
    crossCheck: {
      vistaActualM,
      socJtdM: last.costToDateM,
      timingDeltaM: socDeltaM,
      approvedCOM: meta.approvedCOM,
    },
    health: {
      financial: financialHealth,
      operations: opsHealth,
      schedule: scheduleHealth,
      safety: safetyHealth,
      composite,
    },
    monthly,
    trades,
    ops: {
      rfiMerged: rfi,
      submittalsMerged: submittals,
      changeOrdersMerged: changeOrders,
      inProcore: meta.inProcore,
      rawNote: meta.inProcore
        ? 'Synced counts include Procore + SharePoint reconciliation.'
        : 'Project not in Procore — controls tracked in SharePoint / FileBrain (raw file counts higher than merged entities).',
    },
    safety: {
      soar,
      oshaRecordable: osha,
      firstAidOnly: firstAid,
      lostTimeDays,
      totalIncidents: osha + firstAid + (lostTimeDays > 0 ? 1 : 0),
      trir,
      manHours,
      daysSinceIncident,
      healthScore: safetyHealth,
    },
  };
}

function earnedShare(contractM, pct) {
  return Math.round(contractM * (pct / 100) * 10) / 10;
}

function stdevSample(values) {
  if (!values.length) return 0;
  if (values.length === 1) return 0;
  const mean = values.reduce((s, x) => s + x, 0) / values.length;
  const v = values.reduce((s, x) => s + (x - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(Math.max(0, v));
}

/**
 * Narrative + bullets for the main financial tile (Vista/SOC, margin, billings, WIP).
 * @param {ReturnType<typeof getProjectTwinData>} data
 */
export function buildFinancialTwinInsights(data) {
  const k = data.kpis;
  const x = data.crossCheck;
  const absTiming = Math.abs(x.timingDeltaM);
  const timingTone =
    absTiming >= 6 ? 'risk' : absTiming >= 3 ? 'neutral' : 'positive';
  const bullets = [];

  bullets.push({
    tone: timingTone,
    title: 'System bridge (Vista vs SOC)',
    detail: `Vista actual ($${x.vistaActualM}M) sits ${x.timingDeltaM >= 0 ? 'above' : 'below'} SOC JTD by $${absTiming}M — ${absTiming >= 5 ? 'common when subcontractor accruals post earlier in ERP than SOC close; document the bridge monthly.' : 'within a typical timing band for an integrated close.'}`,
  });

  const marginOk = k.estGrossMarginPct >= 7;
  bullets.push({
    tone: marginOk ? 'positive' : 'risk',
    title: 'Margin trajectory',
    detail: `At ${k.estGrossMarginPct}% est. gross margin on a $${k.revisedContractM}M revised contract, ${marginOk ? 'there is cushion for burn volatility if CPI holds.' : 'margin is thinner — prioritize CO visibility and trade CPI recovery plans.'} Balance to bill is $${k.balanceToBillM}M vs $${k.billedToDateM}M billed.`,
  });

  const wipSign = k.wipM >= 0 ? 'costs in excess' : 'billings in excess';
  bullets.push({
    tone: 'neutral',
    title: 'WIP posture',
    detail: `WIP proxy sits near $${Math.abs(k.wipM)}M (${wipSign} of earned value). Pair with aging AR and retention releases when presenting cash vs P&L.`,
  });

  const recent = data.monthly.slice(-6);
  const avgBurn = recent.length ? recent.reduce((s, r) => s + r.costMonthM, 0) / recent.length : 0;
  const prior = data.monthly.slice(-12, -6);
  const avgPrior = prior.length ? prior.reduce((s, r) => s + r.costMonthM, 0) / prior.length : avgBurn;
  const burnMom = avgPrior > 0 ? ((avgBurn - avgPrior) / avgPrior) * 100 : 0;
  bullets.push({
    tone: burnMom > 15 ? 'risk' : burnMom < -10 ? 'positive' : 'neutral',
    title: 'Burn cadence (6-mo vs prior 6-mo)',
    detail: `Trailing six-month average monthly burn is ~$${avgBurn.toFixed(1)}M, roughly ${burnMom >= 0 ? '+' : ''}${burnMom.toFixed(0)}% versus the prior half-year window — ${burnMom > 12 ? 'watch float on POs and subcontracts.' : burnMom < -8 ? 'reflects smoother installation sequencing or scope stabilization.' : 'consistent with steady execution.'}`,
  });

  const executive = [
    `Financial health scores ${data.health.financial}/100 with $${k.costToDateM}M cost to date (${k.pctComplete}% complete).`,
    `Timing variance between Vista and SOC is $${x.timingDeltaM}M; est. profit at completion is $${k.estProfitM}M.`,
  ].join(' ');

  return { executive, bullets };
}

/**
 * Ops / document-control narrative for merged entity model.
 * @param {ReturnType<typeof getProjectTwinData>} data
 */
export function buildOpsTwinInsights(data) {
  const o = data.ops;
  const rfiPerCo =
    o.changeOrdersMerged > 0 ? Math.round((o.rfiMerged / o.changeOrdersMerged) * 10) / 10 : o.rfiMerged;
  const bullets = [];

  bullets.push({
    tone: o.changeOrdersMerged > 85 ? 'risk' : 'neutral',
    title: 'Design-change density',
    detail: `Merged RFIs (${o.rfiMerged}) vs change orders (${o.changeOrdersMerged}) imply ~${rfiPerCo} RFIs per merged CO — ${o.changeOrdersMerged > 85 ? 'elevated CO counts usually correlate with industrial retrofit complexity or late VE cycles.' : 'within expectations if trade procurement stayed aligned with IFC drops.'}`,
  });

  bullets.push({
    tone: 'neutral',
    title: 'Submittals throughput',
    detail: `${o.submittalsMerged} merged submittals suggest ${o.submittalsMerged > o.rfiMerged * 0.35 ? 'healthy drawing-to-field closure' : 'either front-loaded approvals or a backlog worth trending weekly'} relative to RFIs.`,
  });

  bullets.push({
    tone: data.health.operations >= 80 ? 'positive' : 'neutral',
    title: 'Controls environment',
    detail: data.meta.inProcore
      ? 'Procore-backed workflow — counts reconcile with SharePoint archival for audit.'
      : `${o.rawNote}`,
  });

  const executive = [
    `Operations maturity reads ${data.health.operations}/100 with ${o.rfiMerged} RFIs, ${o.submittalsMerged} submittals, and ${o.changeOrdersMerged} merged change orders.`,
    `Use merged counts for executive dashboards; raw file volumes remain higher in SharePoint/FileBrain.`,
  ].join(' ');

  return { executive, bullets };
}

/**
 * Schedule / EV narrative (SPI, CPI trend, trade spreads).
 * @param {ReturnType<typeof getProjectTwinData>} data
 */
export function buildScheduleTwinInsights(data) {
  const monthly = data.monthly;
  const k = data.kpis;
  if (!monthly.length) {
    return {
      executive: 'No earned-value history at this as-of date — move the calendar forward or pick another project.',
      bullets: [],
    };
  }
  const last = monthly[monthly.length - 1];
  const recent = monthly.slice(-6);
  const prior = monthly.slice(-12, -6);
  const avgKey = (rows, key) =>
    rows.length ? rows.reduce((s, r) => s + r[key], 0) / rows.length : last[key];
  const spi6 = avgKey(recent, 'spi');
  const spiPrev = avgKey(prior, 'spi');
  const cpi6 = avgKey(recent, 'cpi');
  const cpiPrev = avgKey(prior, 'cpi');
  const spiMom = spi6 - spiPrev;
  const cpiMom = cpi6 - cpiPrev;

  const below = data.trades.filter((t) => t.cpi < 1).map((t) => t.name);
  const executive = [
    `Schedule health scores ${data.health.schedule}/100 at ${k.pctComplete}% complete; headline CPI ${last.cpi} and SPI ${last.spi}.`,
    `Trailing six-month average SPI is ${spi6.toFixed(3)} (${spiMom >= 0 ? '+' : ''}${spiMom.toFixed(3)} vs prior half-year); CPI averaged ${cpi6.toFixed(3)} (${cpiMom >= 0 ? '+' : ''}${cpiMom.toFixed(3)}).`,
  ].join(' ');

  const bullets = [];
  bullets.push({
    tone: last.spi >= 1 ? 'positive' : last.spi >= 0.92 ? 'neutral' : 'risk',
    title: 'Earned schedule vs baseline',
    detail: `SPI at ${last.spi} means ${last.spi >= 1 ? 'earned schedule is meeting or beating the performance baseline.' : last.spi >= 0.92 ? 'a manageable lag — tighten lookahead on constraint trades before the next milestone gate.' : 'schedule productivity is trailing; escalate staffing, vendor dates, or logic ties on the critical path.'}`,
  });
  bullets.push({
    tone: last.cpi >= 1 ? 'positive' : last.cpi >= 0.85 ? 'neutral' : 'risk',
    title: 'Portfolio CPI context',
    detail: `Overall CPI is ${last.cpi}. Use the trade CPI bars to explain variance — mechanical trades often lag while commissioning-era support trades rebound.`,
  });
  if (Math.abs(spiMom) >= 0.025 || Math.abs(cpiMom) >= 0.025) {
    bullets.push({
      tone: spiMom >= 0 && cpiMom >= 0 ? 'positive' : spiMom < -0.035 ? 'risk' : 'neutral',
      title: 'Six-month EV momentum',
      detail: `Half-year SPI moved ${spiMom >= 0 ? '+' : ''}${spiMom.toFixed(3)} and CPI ${cpiMom >= 0 ? '+' : ''}${cpiMom.toFixed(3)} versus the prior six months — ${spiMom < -0.035 ? 'schedule recovery should headline the next operating review.' : 'momentum is supportive if risk registers stay current.'}`,
    });
  }
  if (below.length) {
    bullets.push({
      tone: 'risk',
      title: 'Trade CPI watch list',
      detail: `${below.join(', ')} ${below.length > 1 ? 'remain' : 'remains'} below 1.0 CPI — audit productivity hooks, buy-out assumptions, and rework loops on those packages.`,
    });
  } else {
    bullets.push({
      tone: 'positive',
      title: 'Trade CPI alignment',
      detail: 'Sampled trades are at or above 1.0 CPI — protect the trend during punch and turnover when indirects tend to spike.',
    });
  }

  return { executive, bullets };
}

/**
 * Safety / Fulcrum-style narrative (TRIR, severity mix, SOAR culture).
 * @param {ReturnType<typeof getProjectTwinData>} data
 */
export function buildSafetyTwinInsights(data) {
  const s = data.safety;
  const trirOk = s.trir <= 3.5;
  const bullets = [];

  bullets.push({
    tone: s.lostTimeDays > 0 ? 'risk' : 'positive',
    title: 'Lost-time posture',
    detail:
      s.lostTimeDays > 0
        ? `${s.lostTimeDays} lost-time day(s) materially shift severity — run a leadership-visible RCA share-out and re-brief crews before the next production surge.`
        : `Zero lost-time days is the strongest lagging proof point on this card; keep lift plans and energized-work permits tightly coached.`,
  });

  bullets.push({
    tone: trirOk ? 'positive' : 'risk',
    title: 'TRIR vs exposure hours',
    detail: `Approximate TRIR is ${s.trir} on ~${s.manHours.toLocaleString()} man-hours — ${trirOk ? 'directionally aligned with many industrial GC targets, but still slice by trade to catch hotspots.' : 'elevated versus common benchmarks — prioritize high-energy tasks and new-hire cohorts until the trailing rate improves.'}`,
  });

  bullets.push({
    tone: 'neutral',
    title: 'Leading indicators (SOAR)',
    detail: `${s.soar.toLocaleString()} observations logged shows ${s.soar > 12000 ? 'strong field engagement — balance volume with closure discipline so hazards do not age in open status.' : 'steady reporting; encourage supers to log near-misses and positive catches, not only findings.'}`,
  });

  bullets.push({
    tone: s.oshaRecordable > 8 ? 'risk' : 'neutral',
    title: 'Recordable vs first-aid mix',
    detail: `${s.oshaRecordable} OSHA recordable vs ${s.firstAidOnly} first-aid-only events — ${s.oshaRecordable > 8 ? 'the recordable cadence merits targeted trade huddles (struck-by, slips/trips, hand safety).' : 'mix is workable if quarter-over-quarter counts flatten or fall.'}`,
  });

  const executive = [
    `Safety health scores ${s.healthScore}/100 with ${s.totalIncidents} incidents tracked (${s.oshaRecordable} OSHA recordable, ${s.firstAidOnly} first aid).`,
    `${s.soar.toLocaleString()} SOAR observations and ${s.daysSinceIncident} days since last incident frame how proactive vs reactive the culture reads.`,
  ].join(' ');

  return { executive, bullets };
}

function elapsedMonths(meta, asOfYm) {
  const a = Number(asOfYm.slice(0, 4)) * 12 + Number(asOfYm.slice(5, 7));
  const b = Number(meta.startYm.slice(0, 4)) * 12 + Number(meta.startYm.slice(5, 7));
  return Math.max(0, a - b + 1);
}

function buildEmpty(meta, asOfISO, revisedContract) {
  return {
    meta,
    dataAsOf: asOfISO,
    twinForecastThroughYm: null,
    forecastMonthsCount: 0,
    sources: [],
    kpis: {
      revisedContractM: revisedContract,
      contractOriginalM: meta.contractOriginalM,
      approvedCOM: meta.approvedCOM,
      costToDateM: 0,
      estRemainingM: revisedContract,
      pctComplete: 0,
      estProfitM: revisedContract,
      estGrossMarginPct: 0,
      balanceToBillM: revisedContract,
      billedToDateM: 0,
      wipM: 0,
    },
    crossCheck: { vistaActualM: 0, socJtdM: 0, timingDeltaM: 0, approvedCOM: meta.approvedCOM },
    health: { financial: 70, operations: 72, schedule: 70, safety: 85, composite: 74 },
    monthly: [],
    trades: [],
    ops: { rfiMerged: 0, submittalsMerged: 0, changeOrdersMerged: 0, inProcore: meta.inProcore, rawNote: '' },
    safety: {
      soar: 0,
      oshaRecordable: 0,
      firstAidOnly: 0,
      lostTimeDays: 0,
      totalIncidents: 0,
      trir: 0,
      manHours: 0,
      daysSinceIncident: 0,
      healthScore: 85,
    },
  };
}

/**
 * Aggregate monthly rows into calendar quarters (partial current quarter allowed).
 */
export function getQuarterlySummaries(projectId, asOfISO) {
  const data = getProjectTwinData(projectId, asOfISO);
  const revised = data.kpis.revisedContractM;
  const buckets = new Map();

  for (const row of data.monthly) {
    const k = row.quarterKey;
    if (!buckets.has(k)) {
      buckets.set(k, {
        quarterKey: k,
        months: [],
      });
    }
    buckets.get(k).months.push(row);
  }

  const quarterOrder = Array.from(buckets.keys()).sort();
  let prevIncidents = 0;
  let prevObs = 0;

  return quarterOrder.map((qk) => {
    const { months: ms } = buckets.get(qk);
    const costs = ms.map((r) => r.costMonthM);
    const costM = costs.reduce((s, r) => s + r, 0);
    const avgCpi = ms.reduce((s, r) => s + r.cpi, 0) / ms.length;
    const avgSpi = ms.reduce((s, r) => s + r.spi, 0) / ms.length;
    const first = ms[0];
    const last = ms[ms.length - 1];
    const billedQuarterM = Math.round(ms.reduce((s, r) => s + r.billedM, 0) * 10) / 10;
    const billedPctContract =
      revised > 0 ? Math.round((billedQuarterM / revised) * 1000) / 10 : 0;
    const avgMonthlyBurn = ms.length > 0 ? Math.round((costM / ms.length) * 10) / 10 : 0;
    const minMonthCost = costs.length ? Math.round(Math.min(...costs) * 10) / 10 : 0;
    const maxMonthCost = costs.length ? Math.round(Math.max(...costs) * 10) / 10 : 0;
    const costVolatility = Math.round(stdevSample(costs) * 10) / 10;
    const deltaPctInQ = Math.round((last.pctComplete - first.pctComplete) * 10) / 10;
    const burnPerPctPt =
      deltaPctInQ > 0.08 ? Math.round((costM / deltaPctInQ) * 10) / 10 : null;
    const endWipM = Math.round((last.costToDateM - earnedShare(revised, last.pctComplete)) * 10) / 10;
    const avgDocActivity =
      ms.length > 0
        ? Math.round(ms.reduce((s, r) => s + r.docActivityIndex, 0) / ms.length)
        : 0;

    const rq = mulberry32(hashSeed(`${projectId}|${qk}|qops`));
    const rfiQuarter = Math.round(20 + costM * 2.5 + rq() * 42);
    const submittalsQuarter = Math.round(11 + costM * 1.35 + rq() * 28);
    const changeOrdersQuarter = Math.round(5 + costM * 0.85 + rq() * 16);

    const incidentDelta = last.cumulativeIncidents - prevIncidents;
    const obsDelta = last.cumulativeSafetyObs - prevObs;
    prevIncidents = last.cumulativeIncidents;
    prevObs = last.cumulativeSafetyObs;
    const compositeHint = Math.round(
      clamp(50 + avgCpi * 25 + avgSpi * 22 + (last.pctComplete / 100) * 6 - incidentDelta * 2, 0, 100),
    );
    const projectedCount = ms.filter((r) => r.isProjected).length;
    const blend =
      projectedCount === 0 ? 'actual' : projectedCount === ms.length ? 'projected' : 'mixed';
    return {
      quarterKey: qk,
      blend,
      monthsIncluded: ms.length,
      costM: Math.round(costM * 10) / 10,
      avgCpi: Math.round(avgCpi * 1000) / 1000,
      avgSpi: Math.round(avgSpi * 1000) / 1000,
      endPctComplete: last.pctComplete,
      endCostToDateM: last.costToDateM,
      billedQuarterM,
      billedPctContract,
      avgMonthlyBurn,
      minMonthCost,
      maxMonthCost,
      costVolatility,
      deltaPctInQuarter: deltaPctInQ,
      burnPerPctPt,
      endWipM,
      avgDocActivity,
      rfiQuarter,
      submittalsQuarter,
      changeOrdersQuarter,
      incidentDelta,
      obsDelta,
      compositeHint,
    };
  });
}

/**
 * Compact JSON for the digital-twin NL dashboard agent (actuals + quarters + optional projected months).
 */
export function buildTwinAgentSnapshot(projectId, asOfISO) {
  const data = getProjectTwinData(projectId, asOfISO);
  const quarters = getQuarterlySummaries(projectId, asOfISO);
  return {
    dataAsOf: data.dataAsOf,
    twinForecastThroughYm: data.twinForecastThroughYm,
    forecastMonthsCount: data.forecastMonthsCount ?? 0,
    meta: {
      code: data.meta.code,
      name: data.meta.name,
      division: data.meta.division,
      pm: data.meta.pm,
      status: data.meta.status,
    },
    kpis: data.kpis,
    health: data.health,
    ops: data.ops,
    safety: {
      soar: data.safety.soar,
      trir: data.safety.trir,
      manHours: data.safety.manHours,
      daysSinceIncident: data.safety.daysSinceIncident,
      totalIncidents: data.safety.totalIncidents,
      healthScore: data.safety.healthScore,
      oshaRecordable: data.safety.oshaRecordable,
      firstAidOnly: data.safety.firstAidOnly,
    },
    trades: data.trades,
    monthlySeries: data.monthly.slice(-48).map((r) => ({
      month: r.month,
      quarterKey: r.quarterKey,
      costMonthM: r.costMonthM,
      billedM: r.billedM,
      pctComplete: r.pctComplete,
      cpi: r.cpi,
      spi: r.spi,
      docActivityIndex: r.docActivityIndex,
      costToDateM: r.costToDateM,
      billedCumulativeM: r.billedCumulativeM,
      isProjected: Boolean(r.isProjected),
    })),
    quarters: quarters.map((q) => ({
      quarterKey: q.quarterKey,
      blend: q.blend,
      monthsIncluded: q.monthsIncluded,
      costM: q.costM,
      billedQuarterM: q.billedQuarterM,
      billedPctContract: q.billedPctContract,
      avgCpi: q.avgCpi,
      avgSpi: q.avgSpi,
      endPctComplete: q.endPctComplete,
      compositeHint: q.compositeHint,
      endWipM: q.endWipM,
      rfiQuarter: q.rfiQuarter,
      submittalsQuarter: q.submittalsQuarter,
      changeOrdersQuarter: q.changeOrdersQuarter,
    })),
  };
}

/**
 * Rich QoQ interpretation for dashboard copy (deterministic from metrics).
 * @param {ProjectMeta} meta
 */
function buildQuarterInsights(meta, baseline, compare, deltas) {
  const { deltaCost, deltaCpi, deltaSpi, deltaPct, deltaComposite } = deltas;
  const costIntensity = baseline.costM > 0 ? compare.costM / baseline.costM : 1;
  const progressEfficiency =
    deltaPct !== 0 ? deltaCost / deltaPct : null;

  /** @type {{ title: string; detail: string; tone: 'positive'|'risk'|'neutral' }[]} */
  const highlights = [];

  if (deltaCpi >= 0.04) {
    highlights.push({
      tone: 'positive',
      title: 'Cost productivity strengthened',
      detail: `Average CPI moved up by ${deltaCpi.toFixed(3)}, meaning each dollar is buying more verified progress than in ${baseline.quarterKey}. This usually reflects tighter field controls, fewer rework loops, or normalization after a prior overrun quarter.`,
    });
  } else if (deltaCpi <= -0.04) {
    highlights.push({
      tone: 'risk',
      title: 'Cost burn efficiency softened',
      detail: `CPI slipped by ${Math.abs(deltaCpi).toFixed(3)} versus ${baseline.quarterKey}. Worth validating whether this is scope-driven (legitimate CO burn), timing noise between Vista and SOC, or emerging trade-level leakage.`,
    });
  } else {
    highlights.push({
      tone: 'neutral',
      title: 'Cost performance held steady',
      detail: `CPI was broadly flat quarter-over-quarter — no dramatic swing in how efficiently dollars converted to earned progress.`,
    });
  }

  if (deltaSpi >= 0.04) {
    highlights.push({
      tone: 'positive',
      title: 'Schedule cadence improved',
      detail: `SPI improved by ${deltaSpi.toFixed(3)}. The planning curve and field throughput are better aligned; milestone pressure should ease if this holds through the next reporting cycle.`,
    });
  } else if (deltaSpi <= -0.04) {
    highlights.push({
      tone: 'risk',
      title: 'Schedule lag widened',
      detail: `SPI fell by ${Math.abs(deltaSpi).toFixed(3)}. Even if spend is healthy, earned schedule is not keeping up — prioritize lookahead constraints, late vendor deliveries, and turnover on critical path trades.`,
    });
  } else {
    highlights.push({
      tone: 'neutral',
      title: 'Schedule pace stayed comparable',
      detail: `SPI moved only modestly — the job is neither materially catching up nor falling behind versus the prior quarter’s rhythm.`,
    });
  }

  if (deltaCost > 3 && deltaSpi < -0.02) {
    highlights.push({
      tone: 'risk',
      title: '“Heavy spend / soft schedule” pattern',
      detail: `Quarter cost rose about $${deltaCost}M while SPI trended down. That combination often signals acceleration without matching earned schedule — a classic executive-review trigger for ${meta.code}.`,
    });
  }

  if (deltaCost < -3 && deltaSpi >= 0) {
    highlights.push({
      tone: 'positive',
      title: 'Leaner quarter with schedule intact',
      detail: `Spend cooled by roughly $${Math.abs(deltaCost)}M while schedule performance held or improved — a constructive signal for margin and cash timing if quality metrics stayed green.`,
    });
  }

  const incWorse = compare.incidentDelta > baseline.incidentDelta;
  const incBetter = compare.incidentDelta < baseline.incidentDelta;
  if (incWorse && compare.incidentDelta > 0) {
    highlights.push({
      tone: 'risk',
      title: 'Safety incident cadence ticked up',
      detail: `This quarter recorded more incidents than ${baseline.quarterKey}. Pair the SOAR volume (${compare.obsDelta} new observations) with trade-level stand-downs — volume alone does not substitute for closure on hazards.`,
    });
  } else if (incBetter || (compare.incidentDelta === 0 && baseline.incidentDelta > 0)) {
    highlights.push({
      tone: 'positive',
      title: 'Safer operating quarter',
      detail: `Incident counts improved versus ${baseline.quarterKey}, while observations continued (${compare.obsDelta} logged). Reinforce leading-indicator coaching so field teams do not relax vigilance as schedule pressure returns.`,
    });
  } else {
    highlights.push({
      tone: 'neutral',
      title: 'Safety profile comparable',
      detail: `Incident and observation cadence looked broadly similar to ${baseline.quarterKey} — sustain routines rather than pivot hard unless trade mix shifts.`,
    });
  }

  if (progressEfficiency != null && Math.abs(progressEfficiency) > 1.2 && deltaPct > 0) {
    highlights.push({
      tone: 'neutral',
      title: 'Cost-of-progress check',
      detail: `Roughly $${Math.abs(progressEfficiency).toFixed(1)}M moved for each point of % complete gained — use this as a sanity check against your internal benchmarks for ${(meta.division.split('(')[0] || meta.division).trim()}.`,
    });
  }

  const dbill = compare.billedQuarterM - baseline.billedQuarterM;
  if (Math.abs(dbill) >= 0.35) {
    highlights.push({
      tone: dbill > 2 ? 'risk' : dbill < -2 ? 'positive' : 'neutral',
      title: 'Billing throughput moved',
      detail: `Recognized billings ${dbill >= 0 ? 'rose' : 'fell'} (~$${compare.billedQuarterM}M vs $${baseline.billedQuarterM}M), roughly ${compare.billedPctContract}% vs ${baseline.billedPctContract}% of revised contract — align commentary with owner pay-apps, retention, and underbill/overbill posture.`,
    });
  }

  const dVol = compare.costVolatility - baseline.costVolatility;
  if (Math.abs(dVol) >= 0.75) {
    highlights.push({
      tone: dVol > 1.4 ? 'risk' : 'positive',
      title: 'Intra-quarter burn volatility',
      detail: `Month-to-month cost dispersion ${dVol > 0 ? 'widened' : 'tightened'} (σ ≈ $${compare.costVolatility}M vs $${baseline.costVolatility}M; monthly highs $${compare.maxMonthCost}M vs $${baseline.maxMonthCost}M), often driven by ${dVol > 0 ? 'lumpy sub invoices, steel deliveries, or weather windows.' : 'smoother trade sequencing.'}`,
    });
  }

  const docPulse =
    compare.rfiQuarter +
    compare.submittalsQuarter +
    compare.changeOrdersQuarter -
    (baseline.rfiQuarter + baseline.submittalsQuarter + baseline.changeOrdersQuarter);
  highlights.push({
    tone: Math.abs(docPulse) > 45 ? 'risk' : 'neutral',
    title: 'Document-control pulse (merged model)',
    detail: `Compare quarter carried ~${compare.rfiQuarter} RFIs, ${compare.submittalsQuarter} submittals, and ${compare.changeOrdersQuarter} CO actions vs ~${baseline.rfiQuarter} / ${baseline.submittalsQuarter} / ${baseline.changeOrdersQuarter} in ${baseline.quarterKey}. ${docPulse > 30 ? 'Higher volume usually mirrors late-stage rework or owner-directed changes.' : docPulse < -30 ? 'Lower counts merit a quick QA that reviews did not stall.' : 'Governance load looked broadly comparable.'}`,
  });

  const execParts = [];
  execParts.push(
    `Between ${baseline.quarterKey} and ${compare.quarterKey}, ${meta.name} (${meta.code}) saw ${deltaCost >= 0 ? 'higher' : 'lower'} quarterly job cost (${compare.costM}M vs ${baseline.costM}M) with ${deltaPct >= 0 ? 'stronger' : 'weaker'} physical progress at quarter-end (${compare.endPctComplete}% vs ${baseline.endPctComplete}%).`,
  );
  execParts.push(
    `Efficiency reads as ${deltaCpi >= 0 ? 'improving' : 'softening'} on CPI and ${deltaSpi >= 0 ? 'improving' : 'softening'} on SPI; the composite health hint ${deltaComposite >= 0 ? 'rose' : 'fell'} by ${Math.abs(deltaComposite)} points.`,
  );
  execParts.push(
    `Cash-side billings landed near $${compare.billedQuarterM}M (~${compare.billedPctContract}% of revised contract) vs $${baseline.billedQuarterM}M prior quarter; quarter-end WIP proxy shifted from ~$${baseline.endWipM}M to ~$${compare.endWipM}M.`,
  );
  execParts.push(
    `Field engagement on safety ${compare.obsDelta >= baseline.obsDelta ? 'stayed active or increased' : 'eased slightly'} on observations, while incidents ${incWorse ? 'need closer scrutiny' : incBetter ? 'showed improvement' : 'were in line with the prior quarter'}.`,
  );

  /** @type {string | null} */
  let forecastNote = null;
  const bBlend = baseline.blend ?? 'actual';
  const cBlend = compare.blend ?? 'actual';
  if (bBlend !== 'actual' || cBlend !== 'actual') {
    forecastNote =
      'Twin-forward quarters (2026 Q3–Q4 & 2027 past as-of) are deterministic projections — use them to showcase prediction cadence, not as booked actuals.';
    execParts.push(forecastNote);
  }

  /** @type {string[]} */
  const actions = [];
  if (deltaSpi <= -0.03) {
    actions.push('Run a two-week critical-path scrub with procurement — align released drawings, long-lead gear, and trade stacking.');
  }
  if (deltaCpi <= -0.03) {
    actions.push('Request a reconciliation memo between Vista actuals and SOC JTD to isolate timing vs true overrun.');
  }
  if (compare.incidentDelta > baseline.incidentDelta) {
    actions.push('Brief supers on high-change trades (electrical / piping) with a focus on energized work and lift planning.');
  }
  if (deltaComposite >= 8) {
    actions.push('Capture lessons learned while momentum is positive — feed into the next quarterly executive readout.');
  }
  if (actions.length === 0) {
    actions.push('Maintain current reporting rhythm; flag material scope shifts early so CPI/SPI stay interpretable.');
  }

  const verdict =
    deltaComposite >= 6 && deltaCpi >= 0 && deltaSpi >= 0
      ? 'balanced_improvement'
      : deltaComposite <= -6 || deltaSpi <= -0.05
        ? 'elevated_scrutiny'
        : 'mixed';

  const verdictLabel =
    verdict === 'balanced_improvement'
      ? 'Overall: constructive quarter'
      : verdict === 'elevated_scrutiny'
        ? 'Overall: elevated leadership scrutiny warranted'
        : 'Overall: mixed signals — targeted follow-ups';

  return {
    headline: `${compare.quarterKey} vs ${baseline.quarterKey}`,
    subhead: `${meta.code} · ${meta.name}`,
    verdict,
    verdictLabel,
    executiveSummary: execParts.join(' '),
    forecastNote,
    highlights: highlights.slice(0, 11),
    actions: actions.slice(0, 4),
    meta: {
      baselineKey: baseline.quarterKey,
      compareKey: compare.quarterKey,
      costIntensity: Math.round(costIntensity * 100) / 100,
      progressEfficiency: progressEfficiency != null ? Math.round(progressEfficiency * 100) / 100 : null,
    },
  };
}

/**
 * Compare two quarters for narrative + structured deltas (QoQ-style).
 */
export function compareQuarters(projectId, asOfISO, quarterKeyA, quarterKeyB) {
  const rows = getQuarterlySummaries(projectId, asOfISO);
  const a = rows.find((r) => r.quarterKey === quarterKeyA);
  const b = rows.find((r) => r.quarterKey === quarterKeyB);
  if (!a || !b) {
    return {
      ok: false,
      error: 'One or both quarters are outside the available history for this as-of date / project.',
      rows,
    };
  }
  const deltaCost = Math.round((b.costM - a.costM) * 10) / 10;
  const deltaCpi = Math.round((b.avgCpi - a.avgCpi) * 1000) / 1000;
  const deltaSpi = Math.round((b.avgSpi - a.avgSpi) * 1000) / 1000;
  const deltaPct = Math.round((b.endPctComplete - a.endPctComplete) * 10) / 10;
  const deltaComposite = b.compositeHint - a.compositeHint;
  const deltaBilled = Math.round((b.billedQuarterM - a.billedQuarterM) * 10) / 10;
  const deltaWip = Math.round((b.endWipM - a.endWipM) * 10) / 10;
  const deltaVol = Math.round((b.costVolatility - a.costVolatility) * 10) / 10;
  const meta = pickMeta(projectId);
  const narrative = [
    `**${quarterKeyB} vs ${quarterKeyA}** (${meta.code})`,
    '',
    `- **Spend velocity:** ${b.costM}M cost this quarter vs ${a.costM}M prior comparison quarter (${deltaCost >= 0 ? '+' : ''}${deltaCost}M); avg monthly burn $${b.avgMonthlyBurn}M vs $${a.avgMonthlyBurn}M.`,
    `- **Cost dispersion (σ of monthly burns):** $${b.costVolatility}M vs $${a.costVolatility}M (${deltaVol >= 0 ? '+' : ''}${deltaVol}); monthly range $${b.minMonthCost}–$${b.maxMonthCost}M vs $${a.minMonthCost}–$${a.maxMonthCost}M.`,
    `- **Billings recognized:** $${b.billedQuarterM}M (${b.billedPctContract}% of revised contract) vs $${a.billedQuarterM}M (${a.billedPctContract}%) (${deltaBilled >= 0 ? '+' : ''}${deltaBilled}M).`,
    `- **WIP proxy @ quarter-end:** $${b.endWipM}M vs $${a.endWipM}M (${deltaWip >= 0 ? '+' : ''}${deltaWip}M).`,
    `- **Earned value indices (avg):** CPI ${b.avgCpi} vs ${a.avgCpi} (${deltaCpi >= 0 ? '+' : ''}${deltaCpi}); SPI ${b.avgSpi} vs ${a.avgSpi} (${deltaSpi >= 0 ? '+' : ''}${deltaSpi}).`,
    `- **Physical progress:** ${b.endPctComplete}% vs ${a.endPctComplete}% (${deltaPct >= 0 ? '+' : ''}${deltaPct} pts); in-quarter movement ${b.deltaPctInQuarter}% vs ${a.deltaPctInQuarter}%.`,
    `- **Docs & CO (merged est.):** RFIs ${b.rfiQuarter} vs ${a.rfiQuarter}; Submittals ${b.submittalsQuarter} vs ${a.submittalsQuarter}; COs ${b.changeOrdersQuarter} vs ${a.changeOrdersQuarter}.`,
    `- **Safety observations (quarter):** +${b.obsDelta} vs +${a.obsDelta}.`,
    `- **Incidents recorded (quarter):** +${b.incidentDelta} vs +${a.incidentDelta}.`,
    `- **Composite health hint:** ${b.compositeHint} vs ${a.compositeHint} (${deltaComposite >= 0 ? '+' : ''}${deltaComposite}).`,
  ].join('\n');

  const deltas = {
    deltaCost,
    deltaCpi,
    deltaSpi,
    deltaPct,
    deltaComposite,
    deltaBilled,
    deltaWip,
    deltaVol,
  };
  const insights = buildQuarterInsights(meta, a, b, deltas);

  return {
    ok: true,
    a,
    b,
    deltas,
    narrative,
    insights,
    rows,
  };
}

/**
 * Very small NL helper: finds phrases like "Q1 2025", "2025 Q2", "Q3-2024".
 * @returns {{ quarterKeys: string[], message?: string }}
 */
export function parseQuarterQuery(text) {
  const t = text.trim();
  const found = new Set();
  const re1 = /\bQ\s*([1-4])\s*[- ]?\s*(20\d{2})\b/gi;
  const re2 = /\b(20\d{2})\s*[- ]?Q\s*([1-4])\b/gi;
  let m;
  while ((m = re1.exec(t)) !== null) {
    found.add(`${m[2]}-Q${m[1]}`);
  }
  while ((m = re2.exec(t)) !== null) {
    found.add(`${m[1]}-Q${m[2]}`);
  }
  const quarterKeys = Array.from(found).sort(compareQuarterKeys);
  if (quarterKeys.length < 2) {
    return { quarterKeys, message: 'Mention two quarters, e.g. "Q1 2025 vs Q2 2025".' };
  }
  if (quarterKeys.length > 2) {
    return {
      quarterKeys: quarterKeys.slice(0, 2),
      message: 'More than two quarters detected — using the earliest two in chronological order.',
    };
  }
  return { quarterKeys };
}
